/**
 * Application entry point.
 *
 * BrowserRouter, not HashRouter: the URLs in §1 are real paths, and
 * /license/verify/:id has to be shareable without a `#`. This does require the
 * host to serve index.html for unknown paths — the Vite dev server does that
 * already, and the note in vite.config.js records it for deployment.
 */
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import './styles/global.css';
import AppRoutes from './router';
import LiveChatWidget from './components/layout/LiveChatWidget';
import { applyTheme, usePreferencesStore } from './stores/preferencesStore';

/*
  THE SAVED THEME, APPLIED BEFORE THE FIRST RENDER.

  Outside React, and ahead of `createRoot`, because the theme is an attribute on <html>
  rather than anything a component renders — putting it in an effect would paint one
  frame of dark before switching, which is the flash every themed app has to avoid.

  The store has already read storage by the time this line runs (it does so at import),
  so this only mirrors that decision onto the document. Dark needs no attribute at all —
  it is the `:root` block — so a user who has never chosen gets the default with nothing
  happening here, and a failure in this call cannot leave the app unpainted.
*/
applyTheme(usePreferencesStore.getState().theme);

const rootEl = document.getElementById('root');
// Throws with a useful message rather than the bare "Target container is not a
// DOM element" React produces when index.html and this file disagree.
if (!rootEl) throw new Error('Lyrixsa AI: #root element not found in index.html');

createRoot(rootEl).render(
  <StrictMode>
    <BrowserRouter>
      <AppRoutes />
      <LiveChatWidget />
    </BrowserRouter>
  </StrictMode>
);
